import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { Button, Typography } from '@material-tailwind/react';
import { useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import {
  FreeTrialRecitationResult,
  RecitationResultStatusEnum,
} from '../../constants/types/free-trial/type';
import {
  copulaParticle,
  formatToKstDate,
  removeSurname,
} from '../../api/utils/util-func';
import { downloadFile, waitForImages } from '../../utils/free-trial/utils';

interface FreeTrialResultReportProps {
  freeTrialRecitationResult: FreeTrialRecitationResult;
}

const FreeTrialResultReport = ({
  freeTrialRecitationResult,
}: FreeTrialResultReportProps) => {
  const reportRef = useRef<HTMLElement>(null);

  const [isSavingReport, setIsSavingReport] = useState(false);

  const {
    status,
    childName,
    totalScore,
    fluency,
    completeness,
    accuracy,
    pronunciation,
    judgedAt,
  } = freeTrialRecitationResult;

  const isPassed = status === RecitationResultStatusEnum.PASSED;

  const normalizedTotalScore = totalScore ?? 0;

  const overallScoreByCriteria = [
    { title: '유창성', score: fluency ?? 0 },
    { title: '완성도', score: completeness ?? 0 },
    { title: '정확도', score: accuracy ?? 0 },
    { title: '발음', score: pronunciation ?? 0 },
  ];

  const getColorByScore = (score: number) => {
    if (score >= 60) return '#4CAF50';
    if (score >= 30) return '#FFC107';

    return '#FF5722';
  };

  const getAverageResult = (score: number | null) => {
    if (score === null) return 'N/A';
    if (score < 30) return 'Bad';
    if (score < 60) return 'Great';
    return 'Pass';
  };

  const handleSaveReportImage = async () => {
    const report = reportRef.current;

    if (!report || isSavingReport) return;

    try {
      setIsSavingReport(true);

      await document.fonts.ready;
      await waitForImages(report);

      const canvas = await html2canvas(report, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#FFFFFF',
        logging: false,

        onclone: (clonedDocument) => {
          const offsetElements = clonedDocument.querySelectorAll<HTMLElement>(
            '[data-capture-offset]',
          );

          offsetElements.forEach((element) => {
            const offset = Number(element.dataset.captureOffset ?? 0);
            const computedStyle =
              clonedDocument.defaultView?.getComputedStyle(element);
            const currentTransform = computedStyle?.transform;

            element.style.transform =
              currentTransform && currentTransform !== 'none'
                ? `${currentTransform} translateY(${offset}px)`
                : `translateY(${offset}px)`;
          });
        },
      });

      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((blob) => {
          if (!blob) {
            reject(new Error('리포트 이미지 생성에 실패했습니다.'));
            return;
          }

          resolve(blob);
        }, 'image/png');
      });

      const file = new File([blob], `${childName}_AI암송인증리포트.png`, {
        type: 'image/png',
      });

      downloadFile(file);
    } catch (error) {
      console.error('리포트 이미지 저장 실패', error);
    } finally {
      setIsSavingReport(false);
    }
  };

  return (
    <section
      ref={reportRef}
      className="mt-4 rounded-2xl bg-white px-5 py-6 shadow-[0_6px_20px_rgba(40,25,100,0.05)] md:px-7 md:py-7"
    >
      {/* 헤더 */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <Typography
            data-capture-offset="-2"
            className="text-[11px] font-extrabold text-[#6737F4] md:text-[12px]"
          >
            엄마가 보관하는 결과 리포트
          </Typography>

          <Typography
            data-capture-offset="-4"
            className="mt-1 text-[22px] font-black leading-[1.3] text-gray-900 md:text-[26px]"
          >
            {copulaParticle(removeSurname(childName))}의 AI 암송 인증 리포트
          </Typography>

          <Typography className="mt-2 text-[10px] font-medium text-gray-700 md:text-[11px]">
            잉스바 Book 1 · In the beginning ·{' '}
            {formatToKstDate(judgedAt, 'YYYY-MM-DD')}
          </Typography>
        </div>

        <div
          data-html2canvas-ignore="true"
          className="flex shrink-0 flex-col items-stretch gap-2 md:flex-row md:items-end md:gap-1.5"
        >
          <Typography className="text-[10px] font-bold leading-4 text-[#E44234] md:text-[11px]">
            결과 리포트는 확인 후 바로 이미지로 저장해 주세요.
            <br />
            따로 서버에 저장되지 않아 이후에는 다시 저장할 수 없습니다.
          </Typography>

          <Button
            ripple={false}
            disabled={isSavingReport}
            onClick={handleSaveReportImage}
            className="flex h-11 w-full shrink-0 items-center justify-center gap-2 rounded-xl bg-[#211B2D] px-5 py-0 text-[12px] font-bold normal-case text-white shadow-none hover:bg-[#17131F] hover:shadow-none focus:shadow-none disabled:bg-gray-400 md:w-fit md:text-[13px]"
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            {isSavingReport ? '이미지 저장 중...' : '리포트 이미지로 저장'}
          </Button>
        </div>
      </div>

      {/* 암송 평가 */}
      <div className="mt-4">
        <div className="flex flex-col gap-1.5 rounded-xl bg-[#4B7BF5] px-4 py-2 text-white md:flex-row md:items-center md:gap-4">
          <Typography
            data-capture-offset="-8"
            className="shrink-0 text-[14px] font-extrabold text-white md:text-[16px]"
          >
            암송 평가
          </Typography>

          <Typography
            data-capture-offset="-5"
            className="text-[10px] font-medium text-white/90 md:text-[11px]"
          >
            AI가 학생이 원문을 정확하고 완성된 문장으로 암송했는지 진단한
            결과입니다.
          </Typography>
        </div>

        <div className="mt-4 grid gap-4 md:grid-cols-2">
          {/* 암송점수 범위 */}
          <div className="overflow-hidden rounded-xl border border-gray-200">
            <div className="bg-gray-50 px-4 py-2.5">
              <Typography
                data-capture-offset="-6"
                className="text-[11px] font-extrabold text-gray-900 md:text-[12px]"
              >
                암송점수 범위
              </Typography>
            </div>

            <div className="flex flex-col gap-3 p-4">
              <div className="flex items-center gap-3">
                <span className="flex h-7 w-[58px] shrink-0 items-center justify-center rounded-lg bg-[#4CAF50] text-[9px] font-bold text-white md:w-[76px] md:text-[10px]">
                  <span data-capture-offset="-6">60~100점</span>
                </span>

                <Typography
                  data-capture-offset="-6"
                  className="text-[10px] font-medium leading-4 text-gray-700 md:text-[11px]"
                >
                  학습 내용을 정확하고 완성도 있게 암송하였습니다.
                </Typography>
              </div>

              <div className="flex items-center gap-3">
                <span className="flex h-7 w-[58px] shrink-0 items-center justify-center rounded-lg bg-[#FFC107] text-[9px] font-bold text-white md:w-[76px] md:text-[10px]">
                  <span data-capture-offset="-6">30~59점</span>
                </span>

                <Typography
                  data-capture-offset="-6"
                  className="text-[10px] font-medium leading-4 text-gray-700 md:text-[11px]"
                >
                  일부 완성도 보완을 위한 추가적인 연습이 필요합니다.
                </Typography>
              </div>

              <div className="flex items-center gap-3">
                <span className="flex h-7 w-[58px] shrink-0 items-center justify-center rounded-lg bg-[#FF5722] text-[9px] font-bold text-white md:w-[76px] md:text-[10px]">
                  <span data-capture-offset="-6">0~29점</span>
                </span>

                <Typography
                  data-capture-offset="-6"
                  className="text-[10px] font-medium leading-4 text-gray-700 md:text-[11px]"
                >
                  전체 학습 내용에 대한 복습과 암송 연습이 요구됩니다.
                </Typography>
              </div>
            </div>
          </div>

          {/* 평가 기준 안내 */}
          <div className="overflow-hidden rounded-xl border border-gray-200">
            <div className="bg-gray-50 px-4 py-2.5">
              <Typography
                data-capture-offset="-6"
                className="text-[11px] font-extrabold text-gray-900 md:text-[12px]"
              >
                평가 기준 안내
              </Typography>
            </div>

            <div className="flex flex-col gap-3 p-4">
              <div className="flex items-center gap-3">
                <span className="flex h-7 w-[58px] shrink-0 items-center justify-center rounded-lg bg-gray-600 text-[9px] font-bold text-white md:text-[10px]">
                  <span data-capture-offset="-6">유창성</span>
                </span>

                <Typography
                  data-capture-offset="-6"
                  className="text-[10px] font-medium leading-4 text-gray-700 md:text-[11px]"
                >
                  적절한 간격으로 띄어 읽었는지 측정한 결과입니다.
                </Typography>
              </div>

              <div className="flex items-center gap-3">
                <span className="flex h-7 w-[58px] shrink-0 items-center justify-center rounded-lg bg-gray-600 text-[9px] font-bold text-white md:text-[10px]">
                  <span data-capture-offset="-6">완성도</span>
                </span>

                <Typography
                  data-capture-offset="-6"
                  className="text-[10px] font-medium leading-4 text-gray-700 md:text-[11px]"
                >
                  원문과의 일치율을 측정한 결과입니다.
                </Typography>
              </div>

              <div className="flex items-center gap-3">
                <span className="flex h-7 w-[58px] shrink-0 items-center justify-center rounded-lg bg-gray-600 text-[9px] font-bold text-white md:text-[10px]">
                  <span data-capture-offset="-6">정확도</span>
                </span>

                <Typography
                  data-capture-offset="-6"
                  className="text-[10px] font-medium leading-4 text-gray-700 md:text-[11px]"
                >
                  음소 단위로 발음의 정확도를 측정한 결과입니다.
                </Typography>
              </div>

              <div className="flex items-center gap-3">
                <span className="flex h-7 w-[58px] shrink-0 items-center justify-center rounded-lg bg-gray-600 text-[9px] font-bold text-white md:text-[10px]">
                  <span data-capture-offset="-6">발음</span>
                </span>

                <Typography
                  data-capture-offset="-6"
                  className="text-[10px] font-medium leading-4 text-gray-700 md:text-[11px]"
                >
                  영어 발음이 얼마나 명확하고 자연스러운지 측정한 결과입니다.
                </Typography>
              </div>
            </div>
          </div>
        </div>

        {/* 종합 평가 */}
        <div className="mt-7">
          <div className="border-b-2 border-[#4B7BF5] pb-2">
            <Typography
              data-capture-offset="-5"
              className="text-[14px] font-extrabold text-gray-900 md:text-[16px]"
            >
              종합 평가
            </Typography>
          </div>

          <div className="mt-4 rounded-xl border border-gray-200 px-4 py-5 md:px-6 md:py-6">
            <div className="flex flex-col items-center gap-6 md:flex-row md:gap-8">
              {/* 암송점수 원형 차트 */}
              <div className="w-[130px] shrink-0">
                <Typography
                  data-capture-offset="-5"
                  className={`text-center text-xl font-extrabold`}
                  style={{
                    color: getColorByScore(normalizedTotalScore),
                  }}
                >
                  {getAverageResult(normalizedTotalScore)}
                </Typography>

                <div className="relative mt-1 h-32 w-32">
                  <svg
                    width="128"
                    height="128"
                    viewBox="0 0 250 250"
                    className="block"
                  >
                    <circle
                      cx="125"
                      cy="125"
                      r="105"
                      fill="none"
                      stroke="#ECECEC"
                      strokeWidth="12"
                    />

                    <circle
                      cx="125"
                      cy="125"
                      r="105"
                      fill="none"
                      stroke={getColorByScore(normalizedTotalScore)}
                      strokeWidth="12"
                      strokeLinecap="round"
                      strokeDasharray={2 * Math.PI * 105}
                      strokeDashoffset={
                        (1 - normalizedTotalScore / 100) * 2 * Math.PI * 105
                      }
                      style={{
                        transform: 'rotate(-90deg)',
                        transformOrigin: 'center',
                      }}
                    />
                  </svg>

                  <span
                    data-capture-offset="-12"
                    className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-[30px] font-black"
                    style={{
                      color: getColorByScore(normalizedTotalScore),
                    }}
                  >
                    {normalizedTotalScore}
                  </span>
                </div>

                <Typography
                  data-capture-offset="-5"
                  className="mt-1 text-center text-[10px] font-medium text-gray-700 md:text-[11px]"
                >
                  학생 발음 점수
                </Typography>
              </div>

              {/* 평가 기준별 점수 */}
              <div className="flex w-full flex-1 flex-col gap-4">
                {overallScoreByCriteria.map((item) => (
                  <div
                    key={item.title}
                    className="grid grid-cols-[52px_minmax(0,1fr)_28px] items-center gap-3"
                  >
                    <Typography
                      data-capture-offset="-6"
                      className="whitespace-nowrap text-[11px] font-bold text-gray-800 md:text-[12px]"
                    >
                      {item.title}
                    </Typography>

                    <div className="relative h-3 overflow-hidden bg-gray-200">
                      <div
                        className="absolute left-0 top-0 h-full"
                        style={{
                          width: `${item.score}%`,
                          backgroundColor: getColorByScore(item.score),
                        }}
                      />
                    </div>

                    <Typography
                      data-capture-offset="-6"
                      className="text-right text-[10px] font-black md:text-[11px]"
                      style={{
                        color: getColorByScore(item.score),
                      }}
                    >
                      {item.score}
                    </Typography>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FreeTrialResultReport;
